import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../contexts/AuthContext";
import {
  Share2,
  Copy,
  Check,
  Loader2,
  Shield,
  Users,
  Building,
  MapPin,
  Globe,
  Briefcase,
  ArrowRight,
  Lock,
  Unlock,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Send,
  BookmarkPlus,
} from "lucide-react";
import { getApiBaseUrl } from "../../utils/apiBase";
import ShareSignUpCTA from "./ShareSignUpCTA";

const API_BASE_URL = getApiBaseUrl();

const buildShareUrl = (slug, token) => {
  if (!token) return "";
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  const sanitizedOrigin = origin.endsWith("/")
    ? origin.slice(0, origin.length - 1)
    : origin;
  const slugSegment = slug && slug.length > 0 ? `${slug}--${token}` : token;
  return `${sanitizedOrigin}/share/${slugSegment}`;
};

const SummaryStat = ({ icon: Icon, label, value, isPreview }) => (
  <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 shadow-lg shadow-indigo-900/10 backdrop-blur">
    <div className="flex items-center gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-indigo-100/80">
          {label}
        </div>
        <div className="text-lg font-semibold text-white flex items-center gap-2">
          {value}
          {isPreview && <Lock className="h-3 w-3 text-white/50" />}
        </div>
      </div>
    </div>
  </div>
);

const formatNumber = (value) => {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString();
};

// Cache share data using sessionStorage to persist across React tree remounts
const getShareCache = (key) => {
  try {
    const cached = sessionStorage.getItem(`share-cache-${key}`);
    return cached ? JSON.parse(cached) : null;
  } catch { return null; }
};
const setShareCache = (key, data) => {
  try {
    sessionStorage.setItem(`share-cache-${key}`, JSON.stringify(data));
  } catch { /* ignore */ }
};
const deleteShareCache = (key) => {
  try {
    sessionStorage.removeItem(`share-cache-${key}`);
  } catch { /* ignore */ }
};

export default function ShareResultsPage({ mode }) {
  const { slugToken, feedId } = useParams();
  
  // Support both new slugToken format and legacy feed/:feedId format
  // For slugToken: encode it (single path segment)
  // For feedId: use feed/{id} without encoding the slash
  const pathSegment = slugToken 
    ? encodeURIComponent(slugToken) 
    : (feedId ? `feed/${encodeURIComponent(feedId)}` : '');
  
  // Get cached data for this share link
  const cacheKey = `${pathSegment}-${mode}`;
  const cachedData = getShareCache(cacheKey);
  
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth();
  // Initialize from cache to prevent flash of empty state on remount
  const [loading, setLoading] = useState(!cachedData);
  const [shareResponse, setShareResponse] = useState(cachedData || null);
  const [error, setError] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [requiresInvite, setRequiresInvite] = useState(false);
  const [requiresEmail, setRequiresEmail] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isPreview, setIsPreview] = useState(cachedData?.isPreview || false);
  
  // Collaboration state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [votes, setVotes] = useState({ companyVotes: {}, leadVotes: {} });
  const [showNotes, setShowNotes] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  
  // Auto-save state
  const [savedToAccount, setSavedToAccount] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedSearchId, setSavedSearchId] = useState(null);
  const [saveMessage, setSaveMessage] = useState("");

  // Track analytics event
  const trackEvent = useCallback(async (eventType, eventData = null) => {
    if (!pathSegment) return;
    try {
      const encodedPath = pathSegment;
      await fetch(`${API_BASE_URL}/api/share/${encodedPath}/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventType, eventData }),
      });
    } catch (err) {
      // Silently fail analytics
    }
  }, [pathSegment]);

  const shareUrl = useMemo(
    () =>
      buildShareUrl(
        shareResponse?.share?.slug,
        shareResponse?.share?.shareToken || ""
      ),
    [shareResponse?.share?.slug, shareResponse?.share?.shareToken]
  );

  // Fetch public preview or brief (no auth required)
  const fetchPublicPreview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const encodedPath = pathSegment || "";
      // If mode is "brief", fetch the brief endpoint instead
      const endpoint = mode === "brief" ? "brief" : "preview";
      const url = `${API_BASE_URL}/api/share/${encodedPath}/${endpoint}`;

      const response = await fetch(url);
      let data = {};
      try {
        data = await response.json();
      } catch (parseError) {
        console.error("Failed to parse preview response", parseError);
      }

      // Compute cache key inside callback to avoid stale closure
      const currentCacheKey = `${pathSegment}-${mode}`;
      if (!response.ok) {
        setShareResponse(null);
        deleteShareCache(currentCacheKey);
        setRequiresInvite(Boolean(data?.requiresInvite));
        setError(data?.error || "Unable to load this shared list.");
      } else {
        setShareResponse(data);
        setShareCache(currentCacheKey, data); // Cache for remounts
        setIsPreview(Boolean(data?.isPreview));
        setRequiresInvite(Boolean(data?.requiresInvite));
      }
    } catch (err) {
      setShareResponse(null);
      setError(err.message || "Unable to load this shared list.");
    } finally {
      setLoading(false);
    }
  }, [pathSegment, mode]);

  // Fetch authenticated full data
  const fetchFullData = useCallback(
    async (code) => {
      if (!isAuthenticated) {
        return;
      }

      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();
        if (code) params.append("code", code);
        const encodedPath = pathSegment || "";
        const queryString = params.toString();
        const url = `${API_BASE_URL}/api/share/${encodedPath}${
          queryString ? `?${queryString}` : ""
        }`;

        let token;
        try {
          token = await getAccessTokenSilently();
        } catch (tokenError) {
          console.error(
            "Failed to retrieve access token for shared results",
            tokenError
          );
          setLoading(false);
          return;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        let data = {};
        try {
          data = await response.json();
        } catch (parseError) {
          console.error("Failed to parse shared results response", parseError);
        }

        // Compute cache key inside callback to avoid stale closure
        const currentCacheKey = `${pathSegment}-${mode}`;
        if (!response.ok) {
          setRequiresInvite(Boolean(data?.requiresInvite));
          setRequiresEmail(Boolean(data?.requiresEmail));
          setError(data?.error || "Unable to load full data.");
        } else {
          setShareResponse(data);
          setShareCache(currentCacheKey, data); // Cache for remounts
          setIsPreview(false);
          setRequiresInvite(false);
        }
      } catch (err) {
        setError(err.message || "Unable to load this shared list.");
      } finally {
        setLoading(false);
      }
    },
    [pathSegment, mode, isAuthenticated, getAccessTokenSilently]
  );

  // Fetch notes and votes for collaboration
  const fetchCollaboration = useCallback(async () => {
    if (!pathSegment) return;
    const encodedPath = pathSegment;
    
    try {
      const [notesRes, votesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/share/${encodedPath}/notes`),
        fetch(`${API_BASE_URL}/api/share/${encodedPath}/votes`),
      ]);
      
      if (notesRes.ok) {
        const notesData = await notesRes.json();
        setNotes(notesData.notes || []);
      }
      if (votesRes.ok) {
        const votesData = await votesRes.json();
        setVotes({
          companyVotes: votesData.companyVotes || {},
          leadVotes: votesData.leadVotes || {},
        });
      }
    } catch (err) {
      console.error("Failed to fetch collaboration data:", err);
    }
  }, [pathSegment]);

  // Add a note
  const handleAddNote = async () => {
    if (!newNote.trim() || !pathSegment) return;
    setIsAddingNote(true);
    
    try {
      const encodedPath = pathSegment;
      let headers = { "Content-Type": "application/json" };
      
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          headers.Authorization = `Bearer ${token}`;
        } catch (e) {
          // Continue without auth
        }
      }
      
      const res = await fetch(`${API_BASE_URL}/api/share/${encodedPath}/notes`, {
        method: "POST",
        headers,
        body: JSON.stringify({ noteText: newNote.trim() }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setNotes(prev => [data.note, ...prev]);
        setNewNote("");
        trackEvent("add_note");
      }
    } catch (err) {
      console.error("Failed to add note:", err);
    } finally {
      setIsAddingNote(false);
    }
  };

  // Vote on a company or lead
  const handleVote = async (targetType, targetId, vote) => {
    if (!pathSegment) return;
    
    try {
      const encodedPath = pathSegment;
      let headers = { "Content-Type": "application/json" };
      
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          headers.Authorization = `Bearer ${token}`;
        } catch (e) {
          // Continue without auth
        }
      }
      
      const res = await fetch(`${API_BASE_URL}/api/share/${encodedPath}/votes`, {
        method: "POST",
        headers,
        body: JSON.stringify({ targetType, targetId, vote }),
      });
      
      if (res.ok) {
        // Refresh votes
        fetchCollaboration();
        trackEvent("vote", { targetType, targetId, vote });
      }
    } catch (err) {
      console.error("Failed to vote:", err);
    }
  };

  // Initial load - try public preview first (skip if already have cached data)
  useEffect(() => {
    if (authLoading) {
      return;
    }
    
    // Skip fetch if we already have valid cached data (prevents refetch on remount)
    if (cachedData && shareResponse) {
      fetchCollaboration();
      return;
    }
    
    // If authenticated, try to get full data; otherwise get preview
    if (isAuthenticated) {
      fetchFullData();
    } else {
      fetchPublicPreview();
    }
    
    // Fetch collaboration data (notes, votes)
    fetchCollaboration();
  }, [authLoading, isAuthenticated, fetchFullData, fetchPublicPreview, fetchCollaboration, cachedData, shareResponse]);

  // Track page view once data is loaded
  useEffect(() => {
    if (!loading && shareResponse && !hasTrackedView) {
      const eventType = mode === "brief" 
        ? "brief_view" 
        : isPreview 
          ? "preview_view" 
          : "view";
      trackEvent(eventType);
      setHasTrackedView(true);
    }
  }, [loading, shareResponse, hasTrackedView, mode, isPreview, trackEvent]);

  const handleUnlock = async (event) => {
    event.preventDefault();
    const trimmedCode = inviteCode.trim();
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
      return;
    }
    await fetchFullData(trimmedCode);
  };

  const handleSignInToUnlock = () => {
    trackEvent("sign_in_click");
    loginWithRedirect({
      appState: { returnTo: window.location.pathname },
    });
  };

  // Save list to user's account
  const saveToAccount = useCallback(async () => {
    if (!pathSegment || !isAuthenticated || savedToAccount || isSaving) return;
    
    setIsSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/share/${pathSegment}/save`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSavedToAccount(true);
        setSavedSearchId(data.savedSearchId);
        if (data.alreadySaved) {
          setSaveMessage("Already in your lists");
        } else if (data.alreadyOwned) {
          setSaveMessage("This is your list");
        } else {
          setSaveMessage("Saved to your lists");
        }
      }
    } catch (error) {
      console.error("Failed to save list:", error);
    } finally {
      setIsSaving(false);
    }
  }, [pathSegment, isAuthenticated, savedToAccount, isSaving, getAccessTokenSilently]);

  // Auto-save when user authenticates
  useEffect(() => {
    if (isAuthenticated && shareResponse && !savedToAccount && !isSaving) {
      saveToAccount();
    }
  }, [isAuthenticated, shareResponse, savedToAccount, isSaving, saveToAccount]);

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackEvent("copy_link");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy share link:", err);
    }
  };

  const share = shareResponse?.share;
  const search = shareResponse?.search;
  const brief = shareResponse?.brief;
  const isBrief = shareResponse?.isBrief || mode === "brief";
  
  // Handle both regular and brief response formats
  const companies = isBrief 
    ? (shareResponse?.topCompanies || [])
    : (shareResponse?.companies || []);
  const leads = isBrief
    ? (shareResponse?.sampleContacts || [])
    : (shareResponse?.leads || []);
  const counts = shareResponse?.counts || {
    companyCount: companies.length,
    leadCount: leads.length,
    totalCompanies: companies.length,
    totalContacts: leads.length,
  };

  const heroTitle = isBrief
    ? (brief?.title || share?.title || "Buyer Brief")
    : (share?.title || search?.naturalQuery || "Shared results from EQ.app");
  const heroDescription = isBrief
    ? (brief?.summary || share?.description || "A curated brief of your best-fit accounts and contacts.")
    : (share?.description || "Curated results shared securely via EQ.app. Explore the shortlist and collaborate with your team.");

  // SEO meta tags
  const seoTitle = isBrief 
    ? `${heroTitle} - Buyer Brief | EQ.app`
    : `${heroTitle} | EQ.app Shared Research`;
  const seoDescription = heroDescription.slice(0, 160);
  const seoImage = "https://eq.app/og-share-preview.png";
  const canonicalUrl = typeof window !== "undefined" ? window.location.href : "";
  const companyCountText = counts.totalCompanies || counts.companyCount || 0;
  const contactCountText = counts.totalContacts || counts.leadCount || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="EQ.app" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@eq_app" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        
        {/* Article specific */}
        <meta property="article:publisher" content="https://eq.app" />
        
        {/* Rich snippets for search */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": isBrief ? "Report" : "ItemList",
            "name": heroTitle,
            "description": seoDescription,
            "publisher": {
              "@type": "Organization",
              "name": "EQ.app",
              "url": "https://eq.app"
            },
            "numberOfItems": companyCountText + contactCountText,
            "itemListElement": companies.slice(0, 5).map((c, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "item": {
                "@type": "Organization",
                "name": c.name,
                "url": c.website
              }
            }))
          })}
        </script>
      </Helmet>

      <header className="relative overflow-hidden border-b border-white/10 bg-gradient-to-br from-[#5a20ff] via-indigo-600 to-[#4338ca] text-white">
        {/* Grain texture overlay for premium feel */}
        <div className="pointer-events-none absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }} />
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <div className="absolute -top-32 -right-24 h-80 w-80 rounded-full bg-white/20 blur-3xl"></div>
          <div className="absolute bottom-[-20%] left-[-10%] h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl"></div>
        </div>
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl space-y-6">
              {/* Intel Brief Badge - More premium framing */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-white/90 border border-white/10">
                  <Share2 className="h-3.5 w-3.5" />
                  <span>{mode === "brief" ? "Intel Brief" : "Exclusive Research"}</span>
                </div>
                {isPreview && (
                  <span className="rounded-full bg-amber-400/20 border border-amber-400/30 px-3 py-1 text-xs font-medium text-amber-200">
                    Preview Mode
                  </span>
                )}
                {/* Action stats badge - tracking progress */}
                {shareResponse?.analytics?.totalViews > 0 && (
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#52dcb3] animate-pulse" />
                    {shareResponse.analytics.totalViews} viewed
                  </span>
                )}
                {shareResponse?.analytics?.linkCopies > 0 && (
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80 flex items-center gap-1.5">
                    {shareResponse.analytics.linkCopies} shared
                  </span>
                )}
              </div>
              
              <div className="space-y-3">
                <h1 className="text-3xl font-semibold leading-tight sm:text-4xl md:text-[44px] md:leading-[1.05]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {heroTitle}
                </h1>
                <p className="text-base text-indigo-100/90 sm:text-lg">
                  {heroDescription}
                </p>
                {/* Curator attribution - social currency */}
                {share?.curatorName && (
                  <p className="text-sm text-white/70 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-gradient-to-br from-white/30 to-white/10 flex items-center justify-center text-xs font-bold">
                      {share.curatorName.charAt(0).toUpperCase()}
                    </span>
                    Intel curated by <span className="font-semibold text-white">{share.curatorName}</span>
                  </p>
                )}
              </div>
              
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                {/* Saved confirmation badge */}
                {savedToAccount && saveMessage && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#52dcb3]/20 border border-[#52dcb3]/30 px-4 py-2.5 text-sm font-medium text-white">
                    <BookmarkPlus className="h-4 w-4" />
                    {saveMessage}
                  </div>
                )}
                
                {/* Show "Go to your lists" if saved, otherwise show unlock/share buttons */}
                {savedToAccount && savedSearchId ? (
                  <a
                    href={`/app/search/${savedSearchId}`}
                    className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#5a20ff] shadow-lg shadow-indigo-900/20 transition hover:-translate-y-0.5 hover:shadow-xl"
                  >
                    <ArrowRight className="h-4 w-4" />
                    View in Your Lists
                  </a>
                ) : (
                  <>
                    <button
                      onClick={handleCopyLink}
                      className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#5a20ff] shadow-lg shadow-indigo-900/20 transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:bg-white/60 disabled:text-indigo-200"
                      disabled={!shareUrl}
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4" />
                          Link copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Share This Intel
                        </>
                      )}
                    </button>
                    {isPreview && (
                      <button
                        onClick={handleSignInToUnlock}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#52dcb3] to-[#3ecf9e] px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
                      >
                        <Unlock className="h-4 w-4" />
                        Unlock Full Access
                      </button>
                    )}
                  </>
                )}
              </div>
              
              {/* Status message */}
              {isPreview && !savedToAccount && (
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <Lock className="h-4 w-4" />
                  <span>Sign in to unlock full contact details</span>
                </div>
              )}
              {savedToAccount && !isPreview && (
                <div className="flex items-center gap-2 text-sm text-[#52dcb3]">
                  <Sparkles className="h-4 w-4" />
                  <span>Full access unlocked</span>
                </div>
              )}
            </div>
            
            <div className="grid w-full max-w-[380px] gap-4 sm:grid-cols-3 lg:w-[360px] lg:grid-cols-1">
              <SummaryStat
                icon={Building}
                label="Target Companies"
                value={formatNumber(isBrief ? counts.totalCompanies : counts.companyCount)}
              />
              <SummaryStat
                icon={Users}
                label="Key Contacts"
                value={formatNumber(isBrief ? counts.totalContacts : counts.leadCount)}
                isPreview={isPreview}
              />
              <SummaryStat
                icon={Briefcase}
                label="Intel Type"
                value={(search?.leadType || "curated").replace(/_/g, " ").toUpperCase()}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        {loading ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin text-[#5a20ff]" />
            <span className="text-sm">Loading shared list...</span>
          </div>
        ) : requiresInvite && !shareResponse ? (
          <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col items-center gap-3 text-center">
              <Shield className="h-10 w-10 text-[#5a20ff]" />
              <h2 className="text-xl font-semibold text-slate-900">Invite-only list</h2>
              <p className="text-sm text-slate-600">
                {error || "Enter the invite code to unlock this list."}
              </p>
            </div>
            <form onSubmit={handleUnlock} className="mt-6 space-y-4 text-left">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Invite code
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Your access code"
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#5a20ff] focus:ring-2 focus:ring-[#5a20ff]/30"
                  required
                />
              </div>
              {requiresEmail && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  Sign in with the email address that was granted access to this list.
                </div>
              )}
              <button
                type="submit"
                className="w-full rounded-lg bg-[#5a20ff] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#491ad6]"
              >
                Unlock list
              </button>
            </form>
          </div>
        ) : shareResponse ? (
          <div className="space-y-10">
            {/* Brief Summary Section */}
            {isBrief && brief && (
              <section className="rounded-2xl bg-gradient-to-br from-white to-slate-50 p-8 shadow-lg shadow-slate-200/60 ring-1 ring-slate-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center shadow-lg shadow-[#5a20ff]/20">
                    <Sparkles className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-1">
                      Buyer Brief
                    </h2>
                    <p className="text-sm text-slate-500">
                      AI-generated summary based on your research
                    </p>
                  </div>
                </div>
                
                <div className="prose prose-slate max-w-none">
                  <p className="text-base text-slate-700 leading-relaxed">
                    {brief.summary}
                  </p>
                </div>
                
                {brief.searchQuery && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                      Original Search
                    </div>
                    <p className="text-sm text-slate-600 italic">
                      "{brief.searchQuery}"
                    </p>
                  </div>
                )}
                
                <div className="mt-6 flex items-center gap-4">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#5a20ff]/10 rounded-full">
                    <Building className="h-4 w-4 text-[#5a20ff]" />
                    <span className="text-sm font-medium text-[#5a20ff]">
                      {counts.totalCompanies || counts.briefCompanies} companies
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#52dcb3]/10 rounded-full">
                    <Users className="h-4 w-4 text-[#52dcb3]" />
                    <span className="text-sm font-medium text-[#52dcb3]">
                      {counts.totalContacts || counts.briefContacts} contacts
                    </span>
                  </div>
                </div>
              </section>
            )}

            {/* Sign Up CTA Banner - Only show for unauthenticated users in preview mode */}
            {isPreview && !isAuthenticated && !isBrief && (
              <ShareSignUpCTA
                variant="banner"
                resultsCount={counts.totalContacts || counts.leadCount || 0}
                title={heroTitle}
                isAuthenticated={false}
                onSignUp={handleSignInToUnlock}
                className="rounded-2xl overflow-hidden"
              />
            )}

            {/* Companies Section */}
            <section className="grid gap-4 rounded-2xl bg-white p-6 shadow-lg shadow-slate-200/60 ring-1 ring-slate-100">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Company short list
                  </h2>
                  <p className="text-sm text-slate-500">
                    {counts.companyCount > 0
                      ? `${isPreview ? `Showing ${counts.previewCompanyCount || companies.length} of ` : ""}${counts.companyCount} companies curated for this search.`
                      : "No companies saved in this shared list yet."}
                  </p>
                </div>
                {search?.createdAt && (
                  <p className="text-xs text-slate-400">
                    Generated {new Date(search.createdAt).toLocaleString()}
                  </p>
                )}
              </div>
              {companies.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  No companies saved in this shared list yet.
                </div>
              ) : (
                <div className="grid gap-4">
                  {companies.map((company) => {
                    const companyRank =
                      typeof company.search_rank !== "undefined"
                        ? company.search_rank
                        : company.searchRank;
                    return (
                      <div
                        key={`${company.id}-${companyRank || "rank"}`}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm transition hover:border-[#5a20ff]/40 hover:shadow-md"
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="space-y-1">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                              {companyRank ? `Rank ${companyRank}` : "Result"}
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900">
                              {company.name}
                            </h3>
                            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                              {company.city && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                                  <MapPin className="h-3 w-3" />
                                  {[company.city, company.state, company.country]
                                    .filter(Boolean)
                                    .join(", ")}
                                </span>
                              )}
                              {typeof company.totalJobs === "number" && company.totalJobs > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                                  <Briefcase className="h-3 w-3" />
                                  {company.totalJobs} open roles
                                </span>
                              )}
                              {company.industry && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                                  {company.industry}
                                </span>
                              )}
                            </div>
                          </div>
                          {company.website && (
                            <a
                              href={
                                company.website.startsWith("http")
                                  ? company.website
                                  : `https://${company.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm font-semibold text-[#5a20ff] hover:text-[#491ad6]"
                            >
                              Visit site
                              <ArrowRight className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                        <div className="mt-3 grid gap-3 text-xs text-slate-500 sm:grid-cols-3">
                          <div>
                            <div className="font-semibold text-slate-400">Employees</div>
                            <div className="text-slate-700">
                              {company.employeeCount
                                ? formatNumber(company.employeeCount)
                                : "N/A"}
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-slate-400">Lead status</div>
                            <div className="text-slate-700">
                              {company.leadStatus || "Unknown"}
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-slate-400">Last seen</div>
                            <div className="text-slate-700">
                              {company.foundAt
                                ? new Date(company.foundAt).toLocaleString()
                                : "-"}
                            </div>
                          </div>
                        </div>
                        
                        {/* Vote buttons */}
                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleVote("company", company.id, "approve")}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                votes.companyVotes[company.id]?.approve
                                  ? "bg-green-100 text-green-700"
                                  : "bg-slate-100 text-slate-600 hover:bg-green-50 hover:text-green-600"
                              }`}
                            >
                              <ThumbsUp className="h-3.5 w-3.5" />
                              {votes.companyVotes[company.id]?.approve || 0}
                            </button>
                            <button
                              onClick={() => handleVote("company", company.id, "reject")}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                votes.companyVotes[company.id]?.reject
                                  ? "bg-red-100 text-red-700"
                                  : "bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-600"
                              }`}
                            >
                              <ThumbsDown className="h-3.5 w-3.5" />
                              {votes.companyVotes[company.id]?.reject || 0}
                            </button>
                          </div>
                          <span className="text-[10px] text-slate-400">
                            Click to vote
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Contacts Section */}
            <section className="rounded-2xl bg-white p-6 shadow-lg shadow-slate-200/60 ring-1 ring-slate-100">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                    Key contacts
                    {isPreview && <Lock className="h-4 w-4 text-slate-400" />}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {(counts.totalContacts || counts.leadCount || leads.length) > 0
                      ? isPreview 
                        ? `${counts.previewLeadCount || leads.length} of ${counts.totalContacts || counts.leadCount} contacts. Sign in to unlock full details.`
                        : `Top ${counts.totalContacts || counts.leadCount} people surfaced for this search.`
                      : "No contacts were added to this shared list yet."}
                  </p>
                </div>
              </div>
              {leads.length === 0 ? (
                <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  No contacts captured in this list so far.
                </div>
              ) : (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {leads.map((lead) => {
                    const leadRank =
                      typeof lead.search_rank !== "undefined"
                        ? lead.search_rank
                        : lead.searchRank;
                    const isRedacted = lead.isRedacted;
                    return (
                      <div
                        key={`${lead.id}-${leadRank || "lead"}`}
                        className={`rounded-xl border bg-white px-4 py-4 shadow-sm transition hover:shadow-md ${
                          isRedacted 
                            ? "border-amber-200 bg-amber-50/30" 
                            : "border-slate-200 hover:border-[#5a20ff]/40"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                              {leadRank ? `Rank ${leadRank}` : "Contact"}
                              {isRedacted && (
                                <span className="ml-2 text-amber-600">Preview</span>
                              )}
                            </div>
                            <div className="text-base font-semibold text-slate-900 flex items-center gap-2">
                              {lead.name}
                              {isRedacted && <Lock className="h-3 w-3 text-amber-500" />}
                            </div>
                            <div className="text-xs text-slate-500">
                              {lead.jobTitle || "Role unavailable"}
                            </div>
                          </div>
                          {lead.linkedinUrl && !isRedacted ? (
                            <a
                              href={lead.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-semibold text-[#5a20ff] hover:text-[#491ad6]"
                            >
                              View profile
                            </a>
                          ) : isRedacted ? (
                            <button
                              onClick={handleSignInToUnlock}
                              className="text-xs font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1"
                            >
                              <Unlock className="h-3 w-3" />
                              Unlock
                            </button>
                          ) : null}
                        </div>
                        <div className="mt-3 space-y-2 text-xs text-slate-500">
                          <div className="font-semibold text-slate-400">Company</div>
                          <div className="text-slate-700">
                            {lead.companyName || "Unknown company"}
                          </div>
                          {lead.location && (
                            <div>
                              <div className="font-semibold text-slate-400">Location</div>
                              <div className="text-slate-700">{lead.location}</div>
                            </div>
                          )}
                          {lead.managementLevel && (
                            <div>
                              <div className="font-semibold text-slate-400">Level</div>
                              <div className="text-slate-700">
                                {lead.managementLevel}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Unlock CTA for preview - only show if not authenticated */}
              {isPreview && !isAuthenticated && leads.length > 0 && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleSignInToUnlock}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-all shadow-lg shadow-[#5a20ff]/20"
                  >
                    <Unlock className="h-4 w-4" />
                    Unlock All {counts.leadCount} Contacts
                  </button>
                </div>
              )}
            </section>

            {/* Collaboration Section - Notes */}
            <section className="rounded-2xl bg-white p-6 shadow-lg shadow-slate-200/60 ring-1 ring-slate-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-[#5a20ff]" />
                  <h2 className="text-lg font-semibold text-slate-900">
                    Team Notes
                  </h2>
                  {notes.length > 0 && (
                    <span className="px-2 py-0.5 bg-[#5a20ff]/10 text-[#5a20ff] text-xs font-medium rounded-full">
                      {notes.length}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className="text-sm text-[#5a20ff] hover:underline"
                >
                  {showNotes ? "Hide" : "Show"} notes
                </button>
              </div>

              {/* Add note input */}
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note for your team..."
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff]"
                  onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
                />
                <button
                  onClick={handleAddNote}
                  disabled={isAddingNote || !newNote.trim()}
                  className="px-4 py-2.5 bg-[#5a20ff] text-white rounded-xl font-medium text-sm hover:bg-[#491ad6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isAddingNote ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </button>
              </div>

              {/* Notes list */}
              {showNotes && notes.length > 0 && (
                <div className="space-y-3 border-t border-slate-100 pt-4">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#5a20ff]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-[#5a20ff]">
                          {(note.userName || "A")[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-slate-900">
                            {note.userName || "Anonymous"}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(note.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{note.noteText}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showNotes && notes.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4 border-t border-slate-100">
                  No notes yet. Be the first to add one.
                </p>
              )}
            </section>
          </div>
        ) : (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-white text-center text-sm text-slate-500">
            <p>{error || "This shared list is no longer available."}</p>
          </div>
        )}
      </main>

      {/* Sticky CTA for unauthenticated users */}
      {isPreview && !isAuthenticated && !loading && shareResponse && (
        <ShareSignUpCTA
          variant="sticky"
          resultsCount={counts.totalContacts || counts.leadCount || 0}
          title={heroTitle}
          isAuthenticated={false}
          onSignUp={handleSignInToUnlock}
        />
      )}

      <footer className={`border-t border-slate-200 bg-white/70 py-8 text-center text-xs text-slate-500 ${isPreview && !isAuthenticated ? 'pb-24' : ''}`}>
        Powered by{" "}
        <a
          href="https://eq.app"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#5a20ff] hover:text-[#491ad6]"
        >
          EQ.app
        </a>{" "}
        — Create and share intelligent lead lists in seconds.
      </footer>
    </div>
  );
}
